// Packages
import { defineStore } from 'pinia';

// Logic
import filters from '@hub-client/logic/core/filters';

// Stores
import { type Theme, type TimeFormat, useSettings } from '@hub-client/stores/settings';

/**
 * This store is used to exchange messages from global client (parent frame) to hub client (iframe) and the other way around.
 *
 * Before sending and receiving messages is possible, the messagebox needs to be initialised from both sides (parent & hub) which will start a handshake session.
 * If this handshake is ready, the init call will give a Promise after which callbacks can be set for each MessageType.
 *
 * In the next timelime the handshake process is shown:
 *
 * - PARENT:    - Calls `messagebox.init( 'PARENT', 'https://urlofhub' )` which will:
 *              - Add an event listener for messages coming from the hub-client.
 *              - Waits for a 'handshake-start' message from the hub-client.
 *
 * - CHILD:     - Calls `messagebox.init( 'CHILD', 'https://global.pubhubs' )` which will:
 *              - Sends a 'handshake-start' message to the global-client.
 *              - Add an event listener for messages coming from the global client.
 *              - Waits for a 'handshake-ready' message from the global-client.
 *
 * - PARENT:    - Receives the 'handshake-start' message from the hub-client.
 *              - Sends a 'handshake-ready' message to the hub-client.
 *              - Sets internal state so received messages from the hub-client can be processed.
 *              - The `init()` call gives a Promise in which the global-client can set callbacks to certain messagetypes.
 *
 * - CHILD:     - Receices the 'handshake-ready' message from the global-client.
 *              - Sets internal state so received messages from the global-client hub can be processed.
 *              - The `init()` call gives a Promise in which the hub-client can set callbacks to certain messagetypes.
 *
 * Now both clients can send and receive messages to eachother.
 * After a message is received there are some tests done before processing the message:
 *
 * - Is the handshake ready?
 * - Is the message coming from the given url?
 * - Is the message of a known type?
 *
 * If everything is allright, the a callback will be searched for the received messagetype and if it is present, the callback will be called.
 *
 */

/**
 * The id of the iframe used to embed the hub's client.
 */
const iframeHubId = 'hub-frame-id';

/**
 * The id of the iframe used for the miniclients in the global bar.
 */
const miniClientId = 'miniclient-frame-id';

/**
 * Messagebox types
 */
enum MessageBoxType {
	Unset = '',
	Child = 'CHILD',
	Parent = 'PARENT',
}

/**
 * Message types
 */
const modalPrefix = 'dialog-modal';
enum MessageType {
	DialogShowModal = modalPrefix + '-show', // Show modal over bar,
	DialogHideModal = modalPrefix + '-hide', // Hide modal over bar

	Sync = 'sync', // CHILD asks for syncing settings etc.
	UnreadMessages = 'unreadmessages', // Fired (without payload) on read→unread transition so the parent can show a desktop notification
	SendHubInformation = 'sendhubinformation', // Let child ask to send the hubinformation
	HubInformation = 'hubinformation', // Sync hub information (name) with hub client.
	Settings = 'settings', // Sync settings
	RoomChange = 'roomchange', // Change to a room - makes it possible to reflect the room in the url
	AddAccessToken = 'addAccessToken', // Hub frame sends a access token for the global client to store in it's /bar/state.
	AddAuthInfo = 'addAuthInfo', // Hub frame sends its access token and userID to store it in the globalSettings object.
	RemoveAccessToken = 'removeAccessToken', // Hub frame sends a message to remove its' access token to the global client.
	BarShow = 'visibilityBar-show', // Show side bar, mostly relevant for mobile where it can be hidden.
	BarHide = 'visibilityBar-hide',
	EventChange = 'eventchange',
	CloseSidebar = 'closesidebar', // Back navigation in the hub client: close an open forum post or sidebar, else scroll back
	BackState = 'backstate', // Hub → global: whether the hub consumes a back action itself (sidebar or forum post open),
	// so the global client can stop its horizontal scroll from swallowing a back swipe the hub will handle
	ContextMenuOpen = 'contextmenu-open', // Hub asks global to show a context menu
	ContextMenuSelect = 'contextmenu-select', // Global sends selected item index back to hub
	ContextMenuClose = 'contextmenu-close', // Global notifies hub that the context menu was dismissed
	ContextMenuShowModal = 'contextmenu-modal-show', // Hub asks global to dim the bar while a desktop context menu (rendered in the hub iframe) is open
	ContextMenuHideModal = 'contextmenu-modal-hide', // Hub asks global to stop dimming the bar

	ClipboardWrite = 'clipboard-write', // Hub asks global to write text to clipboard (top-level frame has clipboard access; iframes may not)

	// Per-hub key-value store persisted in the global client's localStorage.
	// The hub client's own localStorage is unreliable (blocked in third-party
	// iframes on Safari). The hub loads all data on startup, then sends
	// fire-and-forget updates.
	LocalStoreLoad = 'local-store-load', // Hub → global: request all stored key-value pairs
	LocalStoreLoaded = 'local-store-loaded', // Global → hub: response with all pairs
	LocalStoreUpdate = 'local-store-update', // Hub → global: update { key, value }

	// Miniclient unread state mirroring. A miniclient iframe renders one of two
	// inner components: MiniclientIndependent (runs its own MatrixClient and
	// computes unread state locally) or MiniclientLinked (no MatrixClient;
	// mirrors the aggregate state pushed by the currently-active hub client).
	// HubActive tells a miniclient which of the two to mount; each hub's
	// miniclient switches to Linked when its hub becomes the globally active
	// one. This avoids running two sliding syncs per hub and guarantees only
	// one writer to the LocalStore.
	HubActive = 'hub-active', // Parent → miniclient: { active: boolean }; whether the miniclient's hub is the globally active hub.
	AggregateUnreadState = 'aggregate-unread-state', // Hub-client → parent → matching miniclient: { hubId, state: UnreadState }.
}

/**
 * A message is an object with:
 *
 *  - type - a MessageType. Only messages with a known type can be send and received. Also a callback can be set to a certain message type.
 *  - content - can be anything
 */
class Message {
	type: MessageType;
	content: unknown;

	constructor(type: MessageType, content: unknown = '') {
		this.type = type;
		this.content = content;
	}
}

/**
 * Handshake state
 */
enum HandshakeState {
	Idle = 'idle',
	Started = 'started',
	Ready = 'ready',
}

/**
 * The messagebox itself
 */
const useMessageBox = defineStore('messagebox', {
	state: () => {
		return {
			inIframe: false, // Is the messagebox inside an iframe? Used to determine if a hub is on its own or inside an iframe of the global client.
			type: MessageBoxType.Unset, // Parent or Child
			receiverUrlMap: new Map<string, string>(), // The id of the iframe mapped to the url to which this messagebox can send and receive messages
			handshake: new Map<string, HandshakeState>(), // Handshake state
			callbacks: new Map<string, { [index in MessageType]: (message: Message) => void }>(), // List of callbacks per MessageType
			_windowMessageListener: new Map<string, (event: MessageEvent) => void>(), // Event listener, set at init
			_pendingHandshake: new Map<string, (connected: boolean) => void>(), // Resolver of the in-flight startCommunication per id, so a superseded or torn-down one can be settled
		};
	},

	getters: {
		/**
		 * Handshake is ready (true)
		 */
		isReady: (state) => {
			return (id: string): boolean => {
				return state.handshake.get(id) === HandshakeState.Ready;
			};
		},

		/**
		 * If the hub-client is part of global-client (or standalone, in which case no messages are send/received)
		 */
		isConnected(state): boolean {
			return state.type === MessageBoxType.Parent || state.inIframe;
		},
	},

	actions: {
		/**
		 * Initialize an empty messagebox for parent, hub and miniclients.
		 *
		 * @param type MessageBoxType (PARENT|CHILD)
		 */
		init(type: MessageBoxType) {
			this.reset();
			this.type = type;
		},

		/**
		 * The parent needs to perform a handshake with any of the child messageboxes (hub and miniclients),
		 * before communication can take place.
		 *
		 * @param url url of the other side
		 * @param [id='parentFrame'] the iframe id of the other side (the main frame does not have such an id, so the id will then be 'parentFrame')
		 *
		 * @returns a Promise that resolves true once the handshake is ready. Add callbacks after that.
		 *   Resolves false instead when this handshake is abandoned — superseded by a later
		 *   startCommunication on the same id, or torn down by reset/resetCurrentHub/resetMiniclient —
		 *   in which case the caller must not go on to register callbacks for what is now a stale frame.
		 */
		startCommunication(url: string, id: string = 'parentFrame'): Promise<boolean> {
			// A pending handshake on this id belongs to a frame we are about to stop listening to, so its
			// resolve is unreachable from here on. Settle it as abandoned rather than leaving its caller
			// awaiting forever.
			this.abandonHandshake(id);

			return new Promise((resolve, reject) => {
				this.receiverUrlMap.set(id, url);
				this._pendingHandshake.set(id, resolve);

				// If Child: start handshake with parent
				if (this.inIframe && this.type === MessageBoxType.Child) {
					this.sendMessage(new Message(MessageType.Sync), id);
					this.handshake.set(id, HandshakeState.Started);
				}

				// Start listening
				if (this.isConnected) {
					// The main hub iframe reuses one id across hub switches, and a switch does not go
					// through resetCurrentHub. Drop the previous listener here, or every switch leaves
					// another one attached to window, all of them running on every postMessage.
					const previousListener = this._windowMessageListener.get(id);
					if (previousListener) {
						window.removeEventListener('message', previousListener);
					}

					this._windowMessageListener.set(id, (event: MessageEvent) => {
						if (filters.removeTrailingSlash(event.origin) === filters.removeTrailingSlash(url)) {
							const message = new Message(event.data.type, event.data.content);

							const settings = useSettings();
							// Answer to handshake as parent
							if (message.type === MessageType.Sync && this.type === MessageBoxType.Parent) {
								// console.log('<= ' + this.type + ' RECEIVED handshake:', this.receiverUrl);
								this.sendMessage(
									new Message(MessageType.Settings, {
										theme: settings.theme as Theme,
										timeformat: settings.timeformat as TimeFormat,
										language: settings.language,
									}),
									id,
								);

								this.handshake.set(id, HandshakeState.Ready);
								this._pendingHandshake.delete(id);
								resolve(true);
							}

							// Answer to handshake as child
							else if (message.type === MessageType.Settings && this.type === MessageBoxType.Child) {
								// console.log('=> ' + this.type + ' RECEIVED', HandshakeState.Ready);

								const settingsContent = message.content as { theme: Theme; timeformat: TimeFormat; language: string };
								settings.setTheme(settingsContent.theme);
								settings.setTimeFormat(settingsContent.timeformat);
								settings.setLanguage(settingsContent.language);
								this.handshake.set(id, HandshakeState.Ready);
								this._pendingHandshake.delete(id);
								resolve(true);
							}

							// Normal message was received and is of a known message type
							else if (Object.values(MessageType).includes(message.type)) {
								this.receivedMessage(message, id);
							}
						}
					});

					const listener = this._windowMessageListener.get(id);
					if (listener) {
						window.addEventListener('message', listener);
					}
				} else {
					this._pendingHandshake.delete(id);
					reject(new Error('Messagebox is not connected'));
				}
			});
		},

		/**
		 * Settle a still-pending startCommunication for this id as abandoned, so whoever is awaiting it
		 * stops instead of hanging on a frame we no longer listen to. No-op when there is none.
		 */
		abandonHandshake(id: string) {
			const pending = this._pendingHandshake.get(id);
			if (!pending) return;
			this._pendingHandshake.delete(id);
			pending(false);
		},

		/**
		 * Resets the messagebox. Messages can't be send or received anymore.
		 */
		reset() {
			this.receiverUrlMap.forEach((_, id) => {
				const listener = this._windowMessageListener.get(id);
				if (listener) {
					window.removeEventListener('message', listener);
				}
			});
			[...this._pendingHandshake.keys()].forEach((id) => this.abandonHandshake(id));
			this._windowMessageListener.clear();
			this.inIframe = globalThis.self !== window.top;
			this.type = MessageBoxType.Unset;
			this.receiverUrlMap.clear();
			this.handshake.clear();
			this.callbacks.clear();
		},

		/**
		 * Resets the values for the hub that is currently opened in the iframe.
		 * Messages to and from this hub cannot be sent or received anymore, while messages to and from
		 * the miniclient iframes (for the pinned hubs in the global bar) can still be sent and received.
		 */
		resetCurrentHub() {
			const hubListener = this._windowMessageListener.get(iframeHubId);
			if (hubListener) {
				window.removeEventListener('message', hubListener);
			}
			this.abandonHandshake(iframeHubId);
			this._windowMessageListener.delete(iframeHubId);
			this.receiverUrlMap.delete(iframeHubId);
			this.handshake.delete(iframeHubId);
			this.callbacks.delete(iframeHubId);
		},

		resetMiniclient(id: string) {
			const fullMiniclientId = miniClientId + '_' + id;
			const miniclientListener = this._windowMessageListener.get(fullMiniclientId);
			if (miniclientListener) {
				window.removeEventListener('message', miniclientListener);
			}
			this.abandonHandshake(fullMiniclientId);
			this._windowMessageListener.delete(fullMiniclientId);
			this.receiverUrlMap.delete(fullMiniclientId);
			this.handshake.delete(fullMiniclientId);
			this.callbacks.delete(fullMiniclientId);
		},

		/**
		 * Depending on which client, resolve the target window.
		 * @param [id='parentFrame'] the id of the target window that needs to be resolved
		 * @ignore
		 */
		resolveTarget(id: string = 'parentFrame') {
			const target = {} as { [id: string]: { window: Window; receiverUrl: string } };
			if (this.type === MessageBoxType.Child) {
				const receiverUrl = this.receiverUrlMap.get(id);
				if (receiverUrl) {
					target[id] = { window: window.parent, receiverUrl };
				}
			} else if (id === 'parentFrame') {
				// If the id is 'parentFrame' and the messagebox is of type PARENT, the message needs to be sent to all receivers that are connected with the PARENT.
				this.receiverUrlMap.forEach((receiverUrl, idFromMap) => {
					const el: HTMLIFrameElement | null = document.querySelector('iframe#' + idFromMap);
					if (el !== null && el.contentWindow !== null && el.contentWindow !== undefined) {
						target[idFromMap] = { window: el.contentWindow, receiverUrl };
					}
				});
			} else {
				const el: HTMLIFrameElement | null = document.querySelector('iframe#' + id);
				if (el !== null && el.contentWindow !== null && el.contentWindow !== undefined) {
					const receiverUrl = this.receiverUrlMap.get(id);
					if (receiverUrl) {
						target[id] = { window: el.contentWindow, receiverUrl };
					}
				}
			}
			return target;
		},

		/**
		 * Send a message
		 *
		 * @param message Message
		 * @param [id='parentFrame'] the id of the target that the message needs to be sent to
		 */
		sendMessage(message: Message, id: string = 'parentFrame') {
			if (this.isConnected) {
				const target = this.resolveTarget(id);
				for (const id in target) {
					// console.log('=> ' + this.type + ' SEND', message, target[id].receiverUrl);
					target[id].window.postMessage(message, target[id].receiverUrl);
				}
			}
		},

		/**
		 * Called when a valid message is received
		 * It will call the callback that is set for the MessageType
		 *
		 * @param message Message
		 * @param id id of iframe that the message was received from
		 */
		receivedMessage(message: Message, id: string) {
			if (this.isReady(id)) {
				const callbacks = this.callbacks.get(id);
				const callback = callbacks ? callbacks[message.type] : undefined;
				// console.log('<= ' + this.type + ' RECEIVED', message, id, callback);
				if (callback) {
					callback(message);
				}
			}
		},

		/**
		 * Add a callback to a given MessageType. The callback will be called after that MessageType has been received with the message.
		 *
		 * @param id id of the iframe that the callback belongs to
		 * @param type MessageType
		 * @param callback Function(message)
		 */
		addCallback(id: string, type: MessageType, callback: (message: Message) => void) {
			let callbacksList = this.callbacks.get(id);
			callbacksList ??= {} as { [index in MessageType]: (message: Message) => void };
			callbacksList[type] = callback;
			this.callbacks.set(id, callbacksList);
		},

		/**
		 * Remove a callback
		 *
		 * @param id id of the iframe that the callback belongs to
		 * @param type MessageType
		 */
		removeCallback(id: string, type: MessageType) {
			let callbacksList = this.callbacks.get(id);
			callbacksList ??= {} as { [index in MessageType]: (message: Message) => void };
			delete callbacksList[type];
			this.callbacks.set(id, callbacksList);
		},
	},
});

export { iframeHubId, miniClientId, Message, MessageBoxType, MessageType, useMessageBox };
